import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    Globe, Plus, Trash2, Image, Save, Eye, EyeOff, 
    Package, ChevronDown, ChevronUp, X, Edit2, Upload, Tag, Percent
} from 'lucide-react';
import api from '../lib/api';

// Predefined position names
const POSITION_NAMES = [
    'Oferta zilei',
    'Oferta săptămânii',
    'Promoție flash',
    'Super reducere',
    'Lichidare stoc',
    'Pachet promoțional',
    'Ofertă specială',
    'Best seller',
    'Nou în stoc',
    'Ultimele bucăți'
];

const WebOferte = () => {
    const [offers, setOffers] = useState([
        { id: 'pos1', name: 'Oferta zilei', active: false, products: [] },
        { id: 'pos2', name: 'Promoție flash', active: false, products: [] },
        { id: 'pos3', name: 'Super reducere', active: false, products: [] },
        { id: 'pos4', name: 'Pachet promoțional', active: false, products: [] }
    ]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedOffer, setExpandedOffer] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editingOfferName, setEditingOfferName] = useState(null);
    const [productForm, setProductForm] = useState({
        name: '',
        description: '',
        basePrice: '',      // Original price
        promoPrice: '',     // Promo/package price
        isPackage: false,   // Is this a package price?
        stock: '',          // Available stock
        imageUrl: ''
    });
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadOffers();
    }, []);

    const loadOffers = async () => {
        try {
            const res = await api.get('/web/offers/admin');
            if (res.data && res.data.length > 0) {
                const merged = [
                    { id: 'pos1', name: 'Oferta zilei', active: false, products: [] },
                    { id: 'pos2', name: 'Promoție flash', active: false, products: [] },
                    { id: 'pos3', name: 'Super reducere', active: false, products: [] },
                    { id: 'pos4', name: 'Pachet promoțional', active: false, products: [] }
                ].map(defaultOffer => {
                    const saved = res.data.find(o => o.id === defaultOffer.id);
                    return saved ? { ...defaultOffer, ...saved } : defaultOffer;
                });
                setOffers(merged);
            }
        } catch (err) {
            console.error('Error loading offers:', err);
        } finally {
            setLoading(false);
        }
    };

    const saveOffers = async () => {
        setSaving(true);
        try {
            await api.post('/web/offers', { offers });
            toast.success('Ofertele au fost salvate!');
        } catch (err) {
            toast.error('Eroare la salvare');
        } finally {
            setSaving(false);
        }
    };

    const toggleOfferActive = (offerId) => {
        setOffers(prev => prev.map(o => 
            o.id === offerId ? { ...o, active: !o.active } : o
        ));
    };

    const updateOfferName = (offerId, newName) => {
        setOffers(prev => prev.map(o => 
            o.id === offerId ? { ...o, name: newName } : o
        ));
        setEditingOfferName(null);
    };

    const addProduct = (offerId) => {
        const offer = offers.find(o => o.id === offerId);
        if (offer.products.length >= 10) {
            toast.error('Maximum 10 produse per ofertă');
            return;
        }
        setEditingProduct({ offerId, productIndex: -1 });
        setProductForm({ 
            name: '', 
            description: '', 
            basePrice: '', 
            promoPrice: '', 
            isPackage: false,
            stock: '10',
            imageUrl: '' 
        });
    };

    const editProduct = (offerId, productIndex) => {
        const offer = offers.find(o => o.id === offerId);
        const product = offer.products[productIndex];
        setEditingProduct({ offerId, productIndex });
        setProductForm({
            name: product.name || '',
            description: product.description || '',
            basePrice: product.basePrice?.toString() || product.price?.toString() || '',
            promoPrice: product.promoPrice?.toString() || '',
            isPackage: product.isPackage || false,
            stock: product.stock?.toString() || '10',
            imageUrl: product.imageUrl || ''
        });
    };

    const saveProduct = () => {
        if (!productForm.name.trim()) {
            toast.error('Numele produsului este obligatoriu');
            return;
        }
        if (!productForm.promoPrice) {
            toast.error('Prețul promoțional este obligatoriu');
            return;
        }

        const basePrice = parseFloat(productForm.basePrice) || 0;
        const promoPrice = parseFloat(productForm.promoPrice) || 0;
        const discount = basePrice > 0 ? Math.round(((basePrice - promoPrice) / basePrice) * 100) : 0;

        const newProduct = {
            id: editingProduct.productIndex === -1 ? `prod_${Date.now()}` : offers.find(o => o.id === editingProduct.offerId).products[editingProduct.productIndex].id,
            name: productForm.name.trim(),
            description: productForm.description.trim(),
            basePrice: basePrice,
            price: basePrice, // Keep for backwards compatibility
            promoPrice: promoPrice,
            isPackage: productForm.isPackage,
            discount: discount,
            stock: parseInt(productForm.stock) || 10,
            reservedCount: editingProduct.productIndex === -1 ? 0 : (offers.find(o => o.id === editingProduct.offerId).products[editingProduct.productIndex].reservedCount || 0),
            imageUrl: productForm.imageUrl.trim()
        };

        setOffers(prev => prev.map(o => {
            if (o.id === editingProduct.offerId) {
                const newProducts = [...o.products];
                if (editingProduct.productIndex === -1) {
                    newProducts.push(newProduct);
                } else {
                    newProducts[editingProduct.productIndex] = newProduct;
                }
                return { ...o, products: newProducts };
            }
            return o;
        }));

        setEditingProduct(null);
        toast.success('Produs salvat');
    };

    const deleteProduct = (offerId, productIndex) => {
        if (!window.confirm('Ștergi acest produs?')) return;
        setOffers(prev => prev.map(o => {
            if (o.id === offerId) {
                const newProducts = o.products.filter((_, i) => i !== productIndex);
                return { ...o, products: newProducts };
            }
            return o;
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setProductForm(prev => ({ ...prev, imageUrl: event.target.result }));
        };
        reader.readAsDataURL(file);
    };

    const calculateDiscount = () => {
        const base = parseFloat(productForm.basePrice) || 0;
        const promo = parseFloat(productForm.promoPrice) || 0;
        if (base > 0 && promo > 0 && promo < base) {
            return Math.round(((base - promo) / base) * 100);
        }
        return 0;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl text-white">
                        <Globe size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-zinc-800">Oferte Website</h1>
                        <p className="text-sm text-zinc-500">Gestionează promoțiile afișate pe site</p>
                    </div>
                </div>
                <Button 
                    onClick={saveOffers} 
                    disabled={saving}
                    className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
                >
                    <Save size={18} className="mr-2" />
                    {saving ? 'Se salvează...' : 'Salvează Tot'}
                </Button>
            </div>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-700">
                    <strong>Cum funcționează:</strong> Activează o poziție și adaugă produse cu stoc limitat. 
                    Clienții pot comanda max 1 bucată din fiecare produs. La stoc 0, pot exprima dorința de a comanda.
                </p>
            </div>

            {/* Offers Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {offers.map((offer, index) => (
                    <div 
                        key={offer.id}
                        className={`border rounded-xl overflow-hidden transition-all ${
                            offer.active 
                                ? 'border-emerald-300 bg-emerald-50/50 shadow-lg' 
                                : 'border-zinc-200 bg-white'
                        }`}
                    >
                        {/* Offer Header */}
                        <div 
                            className={`p-4 flex items-center justify-between ${
                                offer.active ? 'bg-emerald-100' : 'bg-zinc-50'
                            }`}
                        >
                            <div className="flex items-center gap-3 flex-1">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${
                                    offer.active 
                                        ? 'bg-emerald-500 text-white' 
                                        : 'bg-zinc-200 text-zinc-500'
                                }`}>
                                    {index + 1}
                                </div>
                                <div className="flex-1">
                                    {editingOfferName === offer.id ? (
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={offer.name}
                                                onChange={(e) => updateOfferName(offer.id, e.target.value)}
                                                className="px-2 py-1 border border-zinc-300 rounded text-sm"
                                                autoFocus
                                            >
                                                {POSITION_NAMES.map(name => (
                                                    <option key={name} value={name}>{name}</option>
                                                ))}
                                            </select>
                                            <Input
                                                value={offer.name}
                                                onChange={(e) => setOffers(prev => prev.map(o => 
                                                    o.id === offer.id ? { ...o, name: e.target.value } : o
                                                ))}
                                                placeholder="Sau scrie denumire..."
                                                className="w-40 h-8 text-sm"
                                            />
                                            <button 
                                                onClick={() => setEditingOfferName(null)}
                                                className="p-1 text-zinc-500 hover:text-zinc-700"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div 
                                            className="cursor-pointer group"
                                            onClick={() => setEditingOfferName(offer.id)}
                                        >
                                            <h3 className="font-semibold text-zinc-800 group-hover:text-purple-600 flex items-center gap-1">
                                                {offer.name}
                                                <Edit2 size={14} className="opacity-0 group-hover:opacity-100 text-purple-500" />
                                            </h3>
                                            <p className="text-xs text-zinc-500">
                                                {offer.products.length} produse
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); toggleOfferActive(offer.id); }}
                                    className={offer.active ? 'border-emerald-400 text-emerald-600' : ''}
                                >
                                    {offer.active ? <Eye size={16} /> : <EyeOff size={16} />}
                                    <span className="ml-1">{offer.active ? 'Activ' : 'Inactiv'}</span>
                                </Button>
                                <button
                                    onClick={() => setExpandedOffer(expandedOffer === offer.id ? null : offer.id)}
                                    className="p-1 text-zinc-500 hover:text-zinc-700"
                                >
                                    {expandedOffer === offer.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                            </div>
                        </div>

                        {/* Expanded Content */}
                        {expandedOffer === offer.id && (
                            <div className="p-4 border-t border-zinc-100">
                                {/* Products List */}
                                {offer.products.length > 0 ? (
                                    <div className="space-y-2 mb-4">
                                        {offer.products.map((product, pIndex) => (
                                            <div 
                                                key={product.id || pIndex}
                                                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-zinc-100"
                                            >
                                                {product.imageUrl ? (
                                                    <img 
                                                        src={product.imageUrl} 
                                                        alt={product.name}
                                                        className="w-12 h-12 object-cover rounded"
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 bg-zinc-100 rounded flex items-center justify-center">
                                                        <Image size={20} className="text-zinc-400" />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium text-zinc-800 truncate">{product.name}</p>
                                                        {product.isPackage && (
                                                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Pachet</span>
                                                        )}
                                                        {product.discount > 0 && (
                                                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded font-bold">
                                                                -{product.discount}%
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm">
                                                        {product.basePrice > 0 && (
                                                            <span className="line-through text-zinc-400">{product.basePrice} RON</span>
                                                        )}
                                                        <span className="font-bold text-red-600">{product.promoPrice} RON</span>
                                                        <span className="text-zinc-400">|</span>
                                                        <span className={`text-xs ${(product.stock - (product.reservedCount || 0)) > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                            Stoc: {product.stock - (product.reservedCount || 0)}/{product.stock}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    <button 
                                                        onClick={() => editProduct(offer.id, pIndex)}
                                                        className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => deleteProduct(offer.id, pIndex)}
                                                        className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-zinc-400 py-4">
                                        Niciun produs adăugat
                                    </p>
                                )}

                                {/* Add Product Button */}
                                {offer.products.length < 10 && (
                                    <Button
                                        variant="outline"
                                        className="w-full border-dashed"
                                        onClick={() => addProduct(offer.id)}
                                    >
                                        <Plus size={18} className="mr-2" />
                                        Adaugă Produs ({offer.products.length}/10)
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Edit Product Modal */}
            <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Package size={20} className="text-purple-600" />
                            {editingProduct?.productIndex === -1 ? 'Adaugă Produs' : 'Editează Produs'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {/* Image */}
                        <div>
                            <Label>Imagine</Label>
                            <div className="mt-1 flex items-center gap-3">
                                {productForm.imageUrl ? (
                                    <img 
                                        src={productForm.imageUrl} 
                                        alt="Preview"
                                        className="w-20 h-20 object-cover rounded-lg border"
                                    />
                                ) : (
                                    <div className="w-20 h-20 bg-zinc-100 rounded-lg flex items-center justify-center border">
                                        <Image size={24} className="text-zinc-400" />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        className="hidden"
                                        onChange={handleImageUpload}
                                    />
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <Upload size={16} className="mr-2" />
                                        Încarcă
                                    </Button>
                                    <p className="text-xs text-zinc-400 mt-1">sau URL:</p>
                                    <Input
                                        value={productForm.imageUrl}
                                        onChange={(e) => setProductForm(p => ({ ...p, imageUrl: e.target.value }))}
                                        placeholder="https://..."
                                        className="mt-1 text-xs"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Name */}
                        <div>
                            <Label>Nume Produs *</Label>
                            <Input
                                value={productForm.name}
                                onChange={(e) => setProductForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="Ex: Gel de duș 500ml"
                                className="mt-1"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <Label>Descriere</Label>
                            <textarea
                                value={productForm.description}
                                onChange={(e) => setProductForm(p => ({ ...p, description: e.target.value }))}
                                placeholder="Descriere scurtă a produsului..."
                                className="mt-1 w-full px-3 py-2 border border-zinc-200 rounded-md text-sm resize-none"
                                rows={2}
                            />
                        </div>

                        {/* Package toggle */}
                        <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <input
                                type="checkbox"
                                id="isPackage"
                                checked={productForm.isPackage}
                                onChange={(e) => setProductForm(p => ({ ...p, isPackage: e.target.checked }))}
                                className="w-4 h-4 text-purple-600"
                            />
                            <label htmlFor="isPackage" className="text-sm text-purple-800 cursor-pointer">
                                <strong>Preț pe pachet</strong> (nu pe bucată)
                            </label>
                        </div>

                        {/* Prices */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Preț Original (RON)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={productForm.basePrice}
                                    onChange={(e) => setProductForm(p => ({ ...p, basePrice: e.target.value }))}
                                    placeholder="0.00"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Preț {productForm.isPackage ? 'Pachet' : 'Promoție'} (RON) *</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={productForm.promoPrice}
                                    onChange={(e) => setProductForm(p => ({ ...p, promoPrice: e.target.value }))}
                                    placeholder="0.00"
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        {/* Discount preview */}
                        {calculateDiscount() > 0 && (
                            <div className="flex items-center justify-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                                <Percent size={18} className="text-red-600" />
                                <span className="text-red-700 font-bold">
                                    Reducere: {calculateDiscount()}%
                                </span>
                            </div>
                        )}

                        {/* Stock */}
                        <div>
                            <Label>Stoc Disponibil</Label>
                            <Input
                                type="number"
                                min="0"
                                value={productForm.stock}
                                onChange={(e) => setProductForm(p => ({ ...p, stock: e.target.value }))}
                                placeholder="10"
                                className="mt-1"
                            />
                            <p className="text-xs text-zinc-500 mt-1">
                                Când stocul ajunge la 0, clienții pot solicita produsul.
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setEditingProduct(null)}
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={saveProduct}
                                className="flex-1 bg-purple-600 hover:bg-purple-700"
                            >
                                Salvează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WebOferte;
